import { useState, useRef, useEffect, KeyboardEvent, useMemo, Fragment } from 'react';
import { useAuthStore } from '../../../stores/auth';
import { useMeasureSearch } from '../../../hooks/useMeasures';
import { incrementMeasureUsage } from '../../../lib/measures';
import { useDebounce } from '../../../hooks/useDebounce';
import type { FindingMeasure } from '@safetyhub/shared';
import { Badge, Button } from '@safetyhub/ui';
import type { SelectedMeasureEntry } from '../../../lib/inspections';
import { WrenchIcon } from '../../icons';

/** 변경 상태 타입 */
type ChangeStatus = 'unchanged' | 'added' | 'removed';

interface MeasureChangeInfo {
  value: string;
  status: ChangeStatus;
}

interface EditableMeasureListProps {
  /** 개선대책 목록 */
  measures: string[];
  /**
   * AI judge 메타데이터 포함 measure 객체 배열 (recommendation.measureItems).
   * 있으면 isPrimary 배지를 표시. measures와 1:1 매핑 가정 (text 기준 매칭).
   */
  measureItems?: FindingMeasure[];
  /** 개선대책 추가 핸들러 (measureId: 라이브러리 선택 시 법적 근거 연동용) */
  onAdd: (measure: string, measureId?: string) => Promise<void>;
  /** 개선대책 삭제 핸들러 */
  onRemove: (index: number) => Promise<void>;
  /** 읽기 전용 */
  disabled?: boolean;
  /** 변경 상태 정보 (원본과 비교) */
  changes?: MeasureChangeInfo[];
  /** 위험 카테고리 (자동완성 필터용 + 피드백 컨텍스트) */
  category?: string;
  /** 중주제 (자동완성 필터용 + 피드백 컨텍스트) */
  subCategory?: string;
  /** HK 대책 개수 (앞쪽 N개가 DB 출처) */
  hkMeasureCount?: number;
  /** 헤더 숨김 (테이블 모드에서 좌측 라벨로 대체) */
  hideLabel?: boolean;
  /** 발견사항 보고서 포함 선택 기능 활성화 여부 */
  findingSelectionEnabled?: boolean;
  /** 선택된 개선대책 목록 (선택자 정보 포함) */
  selectedMeasures?: SelectedMeasureEntry[];
  /** 개선대책 보고서 포함 여부 토글 핸들러 */
  onToggleMeasureSelection?: (measure: string, selected: boolean) => void;
  /** 보고서 생성됨 여부 (체크박스 비활성화) */
  hasReport?: boolean;
  /** 현재 로그인 사용자 ID (본인 선택 해제 권한 판별용) */
  currentUserId?: string;
  /** 현재 사용자가 completion 서명을 마쳤는지 (완료 후 해제 불가) */
  isMyCompleted?: boolean;
  /** 현재 사용자가 점검 참여자 중 PRIMARY 역할인지 (override 권한) */
  isPrimary?: boolean;
  /** 현재 사용자가 조직 Owner인지 (override 권한) */
  isOwner?: boolean;
}

export function EditableMeasureList({
  measures,
  measureItems,
  onAdd,
  onRemove,
  disabled = false,
  changes,
  category,
  subCategory,
  hkMeasureCount,
  hideLabel = false,
  findingSelectionEnabled = false,
  selectedMeasures,
  onToggleMeasureSelection,
  hasReport = false,
  currentUserId,
  isMyCompleted = false,
  isPrimary = false,
}: EditableMeasureListProps) {
  // 개선사항 선택 랭크 'OWNER'는 권한 키가 아니라 **소유자 성질**(서버 `resolveUserRole` ↔ `isOwnerUser`와 짝, P4 ② (b)).
  // 부모가 주는 `isOwner` prop은 점검 관리 권한(inspection.manage)이라 여기서는 읽지 않고 스토어의 성질을 직접 본다
  // (spec들이 `useAuthStore`를 selector 형태로 mock하므로 selector로 읽는다).
  const isOrgOwner = useAuthStore((state) => state.currentMembership?.isOwner === true);

  // measureItems(AI judge 메타) 기반 isPrimary lookup. 없으면 모두 false.
  const primaryMeasureSet = useMemo(() => {
    if (!measureItems || measureItems.length === 0) return new Set<string>();
    return new Set(measureItems.filter((m) => m.isPrimary).map((m) => m.text));
  }, [measureItems]);

  // knowledge backend endpoint는 companyId 기반 — currentMembership에서 추출
  const currentOrganizationId = useAuthStore((state) => state.currentMembership?.companyId ?? null);

  const [isAdding, setIsAdding] = useState(false);
  const [newMeasure, setNewMeasure] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [removingIndex, setRemovingIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // 디바운스된 검색어
  const debouncedSearch = useDebounce(newMeasure, 300);

  // 자동완성 검색
  const { data: searchResults, isLoading: isSearching } = useMeasureSearch(
    currentOrganizationId || undefined,
    {
      search: debouncedSearch,
      category,
      subCategory: subCategory || undefined,
      limit: 10,
    },
    isAdding && debouncedSearch.length >= 1,
  );

  // 평탄화된 제안 목록
  const suggestions = useMemo(() => {
    if (!searchResults) return [];
    const allMeasures = searchResults.flatMap(group => group.measures);
    // 이미 추가된 대책 제외
    return allMeasures.filter(m => !measures.includes(m.measure));
  }, [searchResults, measures]);

  useEffect(() => {
    if (isAdding && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAdding]);

  // 선택 인덱스 리셋
  useEffect(() => {
    setSelectedSuggestionIndex(-1);
  }, [suggestions]);

  // 키보드 탐색 시 선택 항목이 보이도록 스크롤
  useEffect(() => {
    if (selectedSuggestionIndex >= 0 && suggestionsRef.current) {
      const items = suggestionsRef.current.querySelectorAll('button');
      items[selectedSuggestionIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedSuggestionIndex]);

  // 외부 클릭 시 제안 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStartAdd = () => {
    if (disabled) return;
    setIsAdding(true);
    setNewMeasure('');
    setError(null);
    setShowSuggestions(false);
  };

  const handleCancelAdd = () => {
    setIsAdding(false);
    setNewMeasure('');
    setError(null);
    setShowSuggestions(false);
  };

  const handleAdd = async (measureText?: string, measureId?: string) => {
    const trimmed = (measureText || newMeasure).trim();
    if (!trimmed) {
      handleCancelAdd();
      return;
    }

    setIsSaving(true);
    setError(null);
    setShowSuggestions(false);
    try {
      // measureId를 전달하여 법적 근거 연동
      await onAdd(trimmed, measureId);
      // 라이브러리에서 선택한 경우 사용 횟수 증가
      if (measureId && currentOrganizationId) {
        incrementMeasureUsage(currentOrganizationId, measureId).catch(() => {
          // 실패해도 무시
        });
      }
      setNewMeasure('');
      setIsAdding(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : '추가에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectSuggestion = (suggestion: typeof suggestions[0]) => {
    handleAdd(suggestion.measure, suggestion.id);
  };

  const handleRemove = async (index: number) => {
    if (disabled || removingIndex !== null) return;

    setRemovingIndex(index);
    try {
      await onRemove(index);
    } catch (err) {
      setError(err instanceof Error ? err.message : '삭제에 실패했습니다.');
    } finally {
      setRemovingIndex(null);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (showSuggestions) {
        setShowSuggestions(false);
      } else {
        handleCancelAdd();
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (showSuggestions && selectedSuggestionIndex >= 0 && suggestions[selectedSuggestionIndex]) {
        handleSelectSuggestion(suggestions[selectedSuggestionIndex]);
      } else {
        handleAdd();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (suggestions.length > 0) {
        setShowSuggestions(true);
        setSelectedSuggestionIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
    }
  };

  const handleInputChange = (value: string) => {
    setNewMeasure(value);
    setShowSuggestions(value.length >= 1);
  };

  const canToggle = findingSelectionEnabled && !hasReport && !!onToggleMeasureSelection;
  const showSelectionGuide = canToggle && measures.length > 0;
  // measures 배열에 실제로 존재하는 항목만 유효한 선택으로 집계 (AI 재분석 후 stale entry 제외)
  const validSelectedCount = (selectedMeasures || []).filter((sel) => measures.includes(sel.measure)).length;
  const hasNoSelection = showSelectionGuide && validSelectedCount === 0;
  const showReportIndicator = findingSelectionEnabled && hasReport && selectedMeasures && selectedMeasures.length > 0;
  // 점검완료 잠금 시 선택 상태를 읽기 전용으로 표시 (보고서 포함 방식과 동일)
  const showLockedSelection = findingSelectionEnabled && !hasReport && !onToggleMeasureSelection && selectedMeasures && selectedMeasures.length > 0;

  // measure 문자열로 선택 항목 찾기 (권한 판별용)
  const findSelectedEntry = (measure: string): SelectedMeasureEntry | undefined =>
    selectedMeasures?.find((x) => x.measure === measure);

  return (
    <div className={`mb-3 ${showSelectionGuide ? `rounded-card-sm p-2 -m-2 ${hasNoSelection ? 'bg-amber-50 ring-1 ring-amber-300' : 'bg-brand-50/30 ring-1 ring-brand-200'}` : ''}`}>
      {/* 헤더 */}
      {!hideLabel && (
        <div className="text-xs font-medium text-ink-600 mb-1.5 flex items-center gap-1">
          <WrenchIcon className="h-3.5 w-3.5" /> 개선 대책
        </div>
      )}
      {showSelectionGuide && (
        <p className={`text-xs mb-1.5 ${hasNoSelection ? 'text-amber-600 font-medium' : 'text-brand-500'}`}>
          {hasNoSelection
            ? '보고서에 포함할 개선대책을 선택해주세요 (미선택 시 이 위험요인은 보고서에서 제외됩니다)'
            : `${validSelectedCount}개 선택됨`}
        </p>
      )}

      {/* 목록 */}
      {(() => {
        // changes가 있으면 changes 순서대로, 없으면 measures만 표시
        const itemsToRender = changes || measures.map(m => ({ value: m, status: 'unchanged' as ChangeStatus }));
        const hasContent = itemsToRender.length > 0;

        if (!hasContent) {
          return !isAdding && (
            <div className="text-xs text-ink-400 italic ml-4">
              개선대책이 없습니다.
              {!disabled && (
                <Button
                  variant="primary"
                  size="xs"
                  className="ml-1"
                  onClick={handleStartAdd}
                >
                  추가하기
                </Button>
              )}
            </div>
          );
        }

        const hasAiSeparator = hkMeasureCount != null && hkMeasureCount > 0 && hkMeasureCount < itemsToRender.length;

        return (
          <ul className="text-sm text-ink-700 space-y-1 ml-4">
            {itemsToRender.map((item, idx) => {
              const measure = item.value;
              const status = item.status;
              // 삭제된 항목은 현재 measures에서의 인덱스를 찾을 수 없음
              const currentIndex = status !== 'removed'
                ? measures.indexOf(measure)
                : -1;

              const separator = hasAiSeparator && idx === hkMeasureCount ? (
                <li key="hk-ai-separator" className="list-none -ml-4 my-1.5">
                  <hr className="border-line" />
                </li>
              ) : null;

              if (status === 'removed') {
                // 삭제된 항목 렌더링
                return (
                  <Fragment key={`frag-${idx}`}>{separator}<li
                    key={`item-${idx}`}
                    className="flex items-start gap-2 list-disc list-outside text-red-400 line-through"
                  >
                    <span className="flex-1">
                      {measure}
                      <Badge tone="rose" size="sm" className="ml-2 no-underline">
                        -삭제됨
                      </Badge>
                    </span>
                  </li></Fragment>
                );
              }

              // 현재 항목 렌더링
              const selectedEntry = findSelectedEntry(measure);
              const isSelected = !!selectedEntry;
              const showCheckbox = canToggle;
              const isExcludedFromReport = showReportIndicator && !isSelected;
              const isLockedUnselected = showLockedSelection && !isSelected;
              // 해제 권한 판별 — 역할 계층 (Owner > PRIMARY > SECONDARY), 본인 선택, 완료 서명 차단
              const isMySelection = isSelected && !!currentUserId && selectedEntry?.selectedById === currentUserId;
              const selectorCompleted = selectedEntry?.selectedByCompletionStatus === 'COMPLETED';
              const selectorName = selectedEntry?.selectedByName ?? '다른 참여자';
              const selectorRole = selectedEntry?.selectedByRole ?? null;
              const requestorRole: 'OWNER' | 'PRIMARY' | 'SECONDARY' = isOrgOwner
                ? 'OWNER'
                : isPrimary
                  ? 'PRIMARY'
                  : 'SECONDARY';
              const selectorRoleLabel =
                selectorRole === 'OWNER'
                  ? 'Owner'
                  : selectorRole === 'PRIMARY'
                    ? '주 담당자'
                    : selectorRole === 'SECONDARY'
                      ? '참여자'
                      : '이전 참여자';
              // 역할 계층 (높을수록 우위)
              const RANK: Record<'OWNER' | 'PRIMARY' | 'SECONDARY', number> = {
                OWNER: 3,
                PRIMARY: 2,
                SECONDARY: 1,
              };
              const canUnselect = (): boolean => {
                if (!isSelected) return true;
                if (isMySelection) return true;
                if (selectorRole === null) {
                  // orphan(비참여자) — OWNER/PRIMARY만 정리 가능
                  return requestorRole === 'OWNER' || requestorRole === 'PRIMARY';
                }
                return RANK[requestorRole] > RANK[selectorRole];
              };
              let checkboxDisabledReason: string | null = null;
              if (isSelected && isMySelection && isMyCompleted) {
                checkboxDisabledReason =
                  '완료 서명 후에는 해제할 수 없습니다. 먼저 서명을 재설정해주세요.';
              } else if (isSelected && !isMySelection && selectorCompleted && canUnselect()) {
                checkboxDisabledReason = `${selectorName}님이 완료 서명한 항목입니다. 먼저 해당 참여자의 서명을 재설정해주세요.`;
              } else if (isSelected && !isMySelection && !canUnselect()) {
                checkboxDisabledReason = `${selectorName}님(${selectorRoleLabel})이 선택한 항목입니다. 상위 역할자만 해제할 수 있어요.`;
              }
              const isCheckboxDisabled = checkboxDisabledReason !== null;

              return (
                <Fragment key={`frag-${idx}`}>{separator}<li
                  key={`item-${idx}`}
                  className={`
                    group flex items-start gap-2 ${showCheckbox ? 'list-none' : 'list-disc list-outside'}
                    ${removingIndex === currentIndex ? 'opacity-50' : ''}
                    ${status === 'added' ? 'text-green-700' : ''}
                    ${isExcludedFromReport || isLockedUnselected ? 'text-ink-400' : ''}
                  `}
                >
                  {showCheckbox ? (
                    <label
                      className={`flex items-start gap-2 flex-1 ${
                        isCheckboxDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                      }`}
                      title={checkboxDisabledReason ?? undefined}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isCheckboxDisabled}
                        onChange={() => onToggleMeasureSelection?.(measure, !isSelected)}
                        className={`w-3.5 h-3.5 mt-0.5 rounded border-ink-300 text-brand-600 focus:ring-brand-500 flex-shrink-0 ${
                          isCheckboxDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                        }`}
                      />
                      <span className="flex-1 min-w-0">
                        {measure}
                        {primaryMeasureSet.has(measure) && (
                          <span title="AI가 가장 시급하다고 판단한 즉각 조치">
                            <Badge tone="brand" size="sm" className="ml-2">
                              우선
                            </Badge>
                          </span>
                        )}
                        {status === 'added' && (
                          <Badge tone="emerald" size="sm" className="ml-2">
                            +추가됨
                          </Badge>
                        )}
                        {isSelected && selectedEntry?.selectedByName && !isMySelection && (
                          <Badge tone="slate" size="sm" className="ml-2">
                            {selectedEntry.selectedByName} 선택
                          </Badge>
                        )}
                      </span>
                    </label>
                  ) : (
                  <>
                  <span className="flex-1">
                    {measure}
                    {status === 'added' && (
                      <Badge tone="emerald" size="sm" className="ml-2">
                        +추가됨
                      </Badge>
                    )}
                    {(showReportIndicator || showLockedSelection) && isSelected && (
                      <Badge tone="brand" size="sm" className="ml-2">
                        보고서 포함
                      </Badge>
                    )}
                  </span>
                  {!disabled && currentIndex !== -1 && (
                    <button
                      onClick={() => handleRemove(currentIndex)}
                      disabled={removingIndex !== null}
                      className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 p-0.5 -mr-1 transition-opacity"
                      title="삭제"
                    >
                      {removingIndex === currentIndex ? (
                        <div className="animate-spin w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full" />
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  )}
                  </>
                  )}
                </li></Fragment>
              );
            })}
          </ul>
        );
      })()}

      {/* 추가 버튼 (항목 하단) */}
      {!disabled && !isAdding && (
        <button
          onClick={handleStartAdd}
          className="text-xs text-brand-600 hover:text-brand-800 hover:bg-brand-50 px-2 py-1 rounded transition-colors mt-1 ml-4"
        >
          + 추가
        </button>
      )}

      {/* 입력 폼 + 자동완성 */}
      {isAdding && (
        <div className="mt-2 ml-4 relative">
          <input
            ref={inputRef}
            type="text"
            value={newMeasure}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => newMeasure.length >= 1 && setShowSuggestions(true)}
            onBlur={() => {
              // 딜레이를 줘서 제안 클릭이 가능하도록
              setTimeout(() => {
                if (!newMeasure.trim() && !showSuggestions) {
                  handleCancelAdd();
                }
              }, 200);
            }}
            disabled={isSaving}
            placeholder="개선대책 입력 (라이브러리에서 자동완성)"
            className={`
              w-full px-2 py-1 text-sm border rounded-control
              focus:outline-none focus:ring-2 focus:ring-brand-500
              ${isSaving ? 'opacity-50' : ''}
              ${error ? 'border-red-300' : 'border-ink-300'}
            `}
          />

          {/* 자동완성 드롭다운 */}
          {showSuggestions && (suggestions.length > 0 || isSearching) && (
            <div
              ref={suggestionsRef}
              className="absolute z-50 w-full mt-1 bg-white border border-line rounded-card shadow-card max-h-60 overflow-y-auto"
            >
              {isSearching ? (
                <div className="px-3 py-2 text-sm text-ink-500 flex items-center gap-2">
                  <div className="animate-spin w-3 h-3 border-2 border-brand-500 border-t-transparent rounded-full" />
                  검색 중...
                </div>
              ) : (
                suggestions.map((suggestion, idx) => (
                  <button
                    key={suggestion.id}
                    type="button"
                    onClick={() => handleSelectSuggestion(suggestion)}
                    className={`
                      w-full px-3 py-2 text-left text-sm hover:bg-brand-50 border-b border-line-soft last:border-b-0
                      ${selectedSuggestionIndex === idx ? 'bg-brand-50' : ''}
                    `}
                  >
                    <div className="font-medium text-ink-800">{suggestion.measure}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-ink-500">
                        {suggestion.category}
                        {suggestion.subCategory && ` > ${suggestion.subCategory}`}
                      </span>
                      {suggestion.usageCount > 0 && (
                        <Badge tone="slate" shape="rounded" size="sm">
                          사용 {suggestion.usageCount}회
                        </Badge>
                      )}
                      {suggestion.source === 'AI' && (
                        <Badge tone="violet" shape="rounded" size="sm">
                          자동
                        </Badge>
                      )}
                      {suggestion.confidence != null && (
                        <Badge
                          shape="rounded"
                          size="sm"
                          tone={
                            suggestion.confidence >= 0.8 ? 'emerald' :
                            suggestion.confidence >= 0.6 ? 'amber' :
                            'rose'
                          }
                        >
                          {Math.round(suggestion.confidence * 100)}%
                        </Badge>
                      )}
                    </div>
                  </button>
                ))
              )}
              {!isSearching && suggestions.length === 0 && debouncedSearch.length >= 1 && (
                <div className="px-3 py-2 text-sm text-ink-500">
                  일치하는 대책이 없습니다. 직접 입력하세요.
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between mt-1">
            <span className="text-xs text-ink-400">
              {suggestions.length > 0 ? '↑↓ 선택, ' : ''}Enter 저장, Esc 취소
            </span>
            <div className="flex gap-1">
              <button
                onClick={handleCancelAdd}
                disabled={isSaving}
                className="text-xs text-ink-500 hover:text-ink-700 px-2 py-0.5"
              >
                취소
              </button>
              <Button
                variant="primary"
                size="xs"
                onClick={() => handleAdd()}
                disabled={isSaving || !newMeasure.trim()}
              >
                {isSaving ? '저장 중...' : '저장'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 에러 메시지 */}
      {error && (
        <div className="text-xs text-red-500 mt-1 ml-4">{error}</div>
      )}
    </div>
  );
}

export default EditableMeasureList;
